import type { PublicEventView } from "@frontier/content/events";
import type { ShipSnapshot } from "@frontier/protocol/contracts";

export interface EventJoinContext {
  connected: boolean;
  pilot: Pick<ShipSnapshot, "docked" | "dead" | "pending"> | undefined;
  events: readonly Pick<PublicEventView, "id" | "status">[];
  trackedEventId: string | null;
}
const confirmationTimeout = 5000;
const timeoutMessage =
  "Событие не подтвердило выбор. Корабль остаётся на станции. Повтори выбор.";

/** A docked launch follows a confirmed private selection, never the outgoing request. */
export class EventJoin {
  private pending: { eventId: string; deadline: number } | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private readonly send: (
      type: "joinEvent" | "launch",
      targetId?: string,
    ) => void,
    private readonly notice: (message: string) => void,
  ) {}

  request(eventId: string, context: EventJoinContext): boolean {
    this.cancel();
    if (!context.connected || !context.pilot) return false;
    this.notice("");
    if (!eventId) {
      this.send("joinEvent", "");
      return true;
    }
    if (!this.available(context, eventId)) {
      this.notice("Событие больше недоступно. Выбери другое.");
      return false;
    }
    if (!context.pilot.docked) {
      this.send("joinEvent", eventId);
      return true;
    }
    if (context.pilot.dead || context.pilot.pending) return false;
    if (context.trackedEventId === eventId) {
      this.send("launch");
      return true;
    }
    this.pending = { eventId, deadline: Date.now() + confirmationTimeout };
    this.timer = setTimeout(() => {
      this.cancel();
      this.notice(timeoutMessage);
    }, confirmationTimeout);
    this.notice("Подтверждаем маршрут события…");
    this.send("joinEvent", eventId);
    return true;
  }

  receive(context: EventJoinContext): void {
    const pending = this.pending;
    if (!pending) return;
    if (Date.now() >= pending.deadline) {
      this.cancel();
      this.notice(timeoutMessage);
      return;
    }
    if (
      !context.connected ||
      !context.pilot?.docked ||
      context.pilot.dead ||
      context.pilot.pending
    ) {
      this.cancel();
      return;
    }
    if (!this.available(context, pending.eventId)) {
      this.cancel();
      this.notice(
        "Событие завершилось или исчезло. Корабль остаётся на станции.",
      );
      return;
    }
    if (context.trackedEventId !== pending.eventId) return;
    this.cancel();
    this.notice("");
    this.send("launch");
  }

  reject(code: string): void {
    if (code === "EVENT_FULL" || code === "EVENT_UNAVAILABLE") this.cancel();
  }

  cancel(): void {
    this.pending = null;
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
  }

  private available(context: EventJoinContext, eventId: string): boolean {
    return context.events.some(
      (event) =>
        event.id === eventId &&
        (event.status === "available" || event.status === "active"),
    );
  }
}
